import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./PlaylistDetails.css";
import { encodeId } from "../HelperMethods";
import { THEMES, ThemeKey } from "../../types/ThemeTypes";
import {
    Playlist,
    PlaylistSong,
    EnrichedSong,
    PlaylistDetailsProps
} from "../../types/PlaylistTypes";
import TextCardComponent from "../Common/TextCardComponent";
import { authFetch, getCurrentUserId, isAuthenticated } from "../../utils/authUtils";
import { getCacheBustedImageUrl, handleImageError } from "../../utils/imageUtils";
import SongListManager from "./SongListManager";
import { useSongSelection } from "./hooks/useSongSelection";
import { usePlaylistOperations } from "./hooks/usePlaylistOperations";

const PlaylistDetails: React.FC<PlaylistDetailsProps> = ({
    playlistId,
    onEdit,
    onThemeChange
}) => {
    const [playlist, setPlaylist] = useState<Playlist | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const playlistRef = useRef<HTMLDivElement>(null);

    // Use custom hooks
    const songSelection = useSongSelection();
    const playlistOps = usePlaylistOperations();

    // Get current user ID fresh each time
    const getCurrentUser = () => getCurrentUserId();

    useEffect(() => {
        const handleScroll = (e: WheelEvent) => {
            if (playlistRef.current) {
                e.preventDefault();
                playlistRef.current.scrollTop += e.deltaY;
            }
        };

        // Check if the screen width is greater than mobile breakpoint (768px)
        const mediaQuery = window.matchMedia('(min-width: 768px)');

        const handleMediaQueryChange = (e: MediaQueryListEvent) => {
            if (e.matches) {
                window.addEventListener('wheel', handleScroll, { passive: false });
            } else {
                window.removeEventListener('wheel', handleScroll);
            }
        };

        // Initial check
        if (mediaQuery.matches) {
            window.addEventListener('wheel', handleScroll, { passive: false });
        }

        // Listen for changes in screen size
        mediaQuery.addEventListener('change', handleMediaQueryChange);

        return () => {
            window.removeEventListener('wheel', handleScroll);
            mediaQuery.removeEventListener('change', handleMediaQueryChange);
        };
    }, []);

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this playlist? This action cannot be undone.')) {
            return;
        }

        try {
            await playlistOps.deletePlaylist(String(playlistId));
            // Redirect to the profile page after successful deletion
            navigate(`/user/${playlist?.dubu_user}`);
        } catch (err) {
            console.error("Error deleting playlist:", err);
            setError(err instanceof Error ? err.message : String(err));
        }
    };

    useEffect(() => {
        const loadPlaylistDetails = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const { playlist: playlistData, songs } = await playlistOps.loadPlaylistDetails(String(playlistId));
                setPlaylist(playlistData);
                songSelection.setSongs(songs);
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
            } finally {
                setIsLoading(false);
            }
        };

        if (playlistId) {
            loadPlaylistDetails();
        }
    }, [playlistId]);

    // Check authentication status and redirect if needed
    useEffect(() => {
        const checkAuth = async () => {
            try {
                const response = await authFetch('/api/auth/check');
                if (!response.ok) {
                    // User is not authenticated on backend, clear localStorage and redirect to login
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    navigate('/login');
                }
            } catch (err) {
                console.error('Error checking auth:', err);
            }
        };

        // Only check if we think we're authenticated
        if (isAuthenticated()) {
            checkAuth();
        }
    }, [navigate]);

    // Notify parent about theme when playlist loads
    useEffect(() => {
        if (playlist && onThemeChange) {
            onThemeChange(playlist.theme as ThemeKey);
        }
    }, [playlist, onThemeChange]);

    const formatDate = (dateString: string) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    if (isLoading) {
        return <div className="playlist-details loading">Loading playlist...</div>;
    }

    if (error || playlistOps.error) {
        return <div className="playlist-details error">Error: {error || playlistOps.error}</div>;
    }

    if (!playlist) {
        return <div className="playlist-details not-found">Playlist not found</div>;
    }

    return (
        <div className="playlist-details" ref={playlistRef}>

            <div className="playlist-header">
                <h6 className="playlist-header-left">
                    <span className={`visibility-badge ${playlist.is_public ? 'public' : 'private'}`}>
                        {playlist.is_public ? 'Public' : 'Private'}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center' }}> by {playlist &&
                        <><Link to={`/user/${playlist.dubu_user}`}>
                            <img src={getCacheBustedImageUrl(playlist.picture)}
                                alt="profile" className="profile-image-icon"
                                onError={handleImageError} />
                        </Link><b><Link to={`/user/${playlist.dubu_user}`} className="user-link">
                            {playlist.dubu_user}
                        </Link></b></>}</div> on {formatDate(playlist.created_at)}
                </h6>
                {/* <h5>Tags here</h5> */}
                <div className="playlist-actions">

                    {Number(playlist.user_id) === Number(getCurrentUser()) && (
                        <>
                            {onEdit && (
                                <button className="edit-button" onClick={onEdit}>
                                    <i className="fa fa-pencil-square-o" aria-hidden="true"></i>
                                </button>
                            )}
                            <button
                                className="delete-button"
                                onClick={handleDelete}
                                disabled={playlistOps.isLoading}
                            >
                                <i className="fa fa-trash-o" aria-hidden="true"></i>
                            </button>
                        </>
                    )}
                </div>

            </div >

            <div className="playlist-desc">
                <div className="playlist-header-text">
                    <div className="main-text"><strong>{playlist.name}</strong></div>
                    {playlist.description && <h6><i>{playlist.description}</i></h6>}
                </div>
                <img className="dubu-music-image" src={playlist ? THEMES[playlist.theme as keyof typeof THEMES].image : THEMES.SING.image}
                    alt="dubu"
                />
            </div>
            <div className="playlist-container">

                <div className="playlist-container-left">

                    <div className="songs-container">
                        <SongListManager
                            songs={songSelection.enrichedSongs}
                            isEditable={false}
                            emptyMessage="No songs in this playlist yet."
                            showNumbers={true}
                        />
                    </div>


                </div>
                {/* <div className="playlist-container-right">
                    <TextCardComponent description={`Created: ${formatDate(playlist.created_at)} Updated: ${formatDate(playlist.updated_at)}`} />

                </div> */}
            </div>
            <br></br>
        </div >
    );
};

export default PlaylistDetails;
